import { Badge, Group, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import type { User } from '@opsopti/shared'
import { getHasManyJoinIds, useGlobalState, userSchema, useUsersQuery, useUserUpdateMutation } from '@opsopti/shared'
import type { PayloadFindRequestParams } from '@opsopti/shared/src/services/api/api.types'
import { IconMapPin, IconUsers } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { useRef } from 'react'

import type { TableProps } from '@/components/Table'
import {
  CellTextWithTooltip,
  EditTextarea,
  HeaderCellWithTooltip,
  Table,
  TableRowActionButton,
} from '@/components/Table'
import { tableScreenIcon } from '@/screens/TableScreen'
import { UserFormScreen } from '@/screens/UserFormScreen'
import { getFormScreenTitle, showErrorNotification, showSuccessNotification } from '@/utils/componentCommons'

import { AssetAssignmentsTable } from './AssetAssignmentsTable'
import { ImportModal, type ImportModalRef } from './ImportModal'

type UsersTableProps = {
  where?: PayloadFindRequestParams['where']
  TableProps?: Omit<TableProps<User>, 'data' | 'columns'>
}

export const UsersTable = observer(function UsersTable(props: UsersTableProps) {
  const importModal = useRef<ImportModalRef>(null)

  const { api, uiStore } = useGlobalState()
  const usersQuery = useUsersQuery({
    params: {
      where: props.where,
      select: {
        visibilityStatus: true,
        accountStatus: true,
        firstName: true,
        lastName: true,
        fullName: true,
        email: true,
        internalNotes: true,
        memberNotes: true,
        role: true,
        createdAssetAssignments: true,
        assignedAssetAssignments: true,
      },
    },
  })
  const userUpdateMutation = useUserUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  function archive(user: User) {
    modals.openConfirmModal({
      title: 'Archive User?',
      centered: true,
      children: <Text size="sm">{`Are you sure you want to archive this User?`}</Text>,
      labels: { confirm: 'Archive User', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () =>
        userUpdateMutation.mutate({ id: user.id, visibilityStatus: 'archived', accountStatus: 'disabled' }),
    })
  }

  function resetPassword(user: User) {
    modals.openConfirmModal({
      title: 'Send Reset Password Email?',
      centered: true,
      children: <Text size="sm">{`Are you sure you want to send a reset password email to ${user.email}?`}</Text>,
      labels: { confirm: 'Send Email', cancel: 'Cancel' },
      groupProps: { mt: 'xl' },
      onConfirm: async () => {
        const response = await api.safeRequest(api.forgotPassword, { email: user.email })

        if (response.ok) {
          showSuccessNotification({ title: 'Password Change Requested!' })
        } else {
          showErrorNotification({ title: 'Error Sending Password Change Email', message: response.message })
        }
      },
    })
  }

  return (
    <>
      <Table
        columns={groupsCols}
        data={usersQuery.data?.docs ?? []}
        state={{ isLoading: usersQuery.isLoading }}
        entity={{ singular: 'User', plural: 'Users', Icon: IconUsers }}
        displayColumnDefOptions={{ 'mrt-row-actions': { size: 138 } }}
        enableTableHead={!!usersQuery.data?.docs?.length}
        // pagination
        initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
        enablePagination
        enableBottomToolbar
        manualPagination={false}
        // editing
        enableEditing
        editDisplayMode="cell"
        // row actions
        rowActionsInclude={['edit', 'resetPassword', 'archive']}
        rowActionsTooltipLabels={{
          edit: `Edit User`,
          archive: 'Archive User',
          resetPassword: 'Send Password Reset Email',
        }}
        rowActionsButtonProps={row => {
          if (row.original.visibilityStatus === 'archived') {
            return { archive: { disabled: true } }
          }
        }}
        onRowActionClick={(action, row) => {
          if (action === 'archive') {
            archive(row.original)
          } else if (action === 'resetPassword') {
            resetPassword(row.original)
          } else if (action === 'edit') {
            uiStore.openFormDrawer({
              Component: UserFormScreen,
              ComponentProps: { params: { id: row.original.id }, goBackFn: uiStore.closeFormDrawer, type: 'drawer' },
              TitleProps: {
                Icon: tableScreenIcon['users'],
                title: getFormScreenTitle(row.original.id, 'User', row.original.fullName ?? ''),
              },
            })
          }
        }}
        // toolbar
        toolbarActionsInclude={['new', 'import', 'export', 'heightToggle']}
        toolbarActionsState={{ heightToggle: uiStore.tableHeightState['users'] }}
        onToolbarActionClick={action => {
          if (action === 'new') {
            uiStore.openFormDrawer({
              Component: UserFormScreen,
              ComponentProps: { goBackFn: uiStore.closeFormDrawer, type: 'drawer' },
              TitleProps: {
                Icon: tableScreenIcon['users'],
                title: getFormScreenTitle(null, 'User'),
              },
            })
          } else if (action === 'heightToggle') {
            uiStore.toggleTableHeight('users')
          } else if (action === 'import') {
            importModal.current?.open()
          }
        }}
        {...props.TableProps}
      />

      <ImportModal schema="user" importModalRef={importModal} />
    </>
  )
})

const columnHelper = createMRTColumnHelper<User>()

const groupsCols: MRT_ColumnDef<User>[] = [
  columnHelper.accessor('visibilityStatus', {
    header: 'Visibility',
    enableEditing: false,
    grow: false,
    size: 174,
    filterVariant: 'select',
    mantineFilterSelectProps: { data: Object.values(userSchema.shape.visibilityStatus.enum) },
    Cell: props => {
      const status = props.cell.getValue()

      return (
        <Badge
          variant="filled"
          color={
            {
              internal: 'cornflower',
              public: 'green',
              archived: 'red',
            }[status]
          }>
          {status}
        </Badge>
      )
    },
  }),
  columnHelper.accessor('fullName', {
    header: 'Name',
    enableEditing: false,
    grow: true,
    mantineTableBodyCellProps: { style: { fontWeight: 600 } },
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    enableEditing: false,
    grow: true,
    mantineTableBodyCellProps: { style: { fontWeight: 600 } },
  }),
  columnHelper.accessor('role', {
    header: 'Role',
    enableEditing: false,
    // grow: false,
    size: 116,
    filterVariant: 'select',
    mantineFilterSelectProps: { data: Object.values(userSchema.shape.role.enum) },
  }),
  columnHelper.accessor(
    row => {
      const cAssignments = getHasManyJoinIds(row.createdAssetAssignments)
      const aAssignments = getHasManyJoinIds(row.assignedAssetAssignments)

      return new Set([...cAssignments, ...aAssignments]).size
    },
    {
      id: 'assigned',
      header: '# Assignments',
      grow: false,
      enableSorting: false,
      enableEditing: false,
      enableColumnFilter: false,
      size: 160,
      Cell: ({ cell, row }) => {
        const { uiStore } = useGlobalState()

        const count = cell.getValue()

        if (!count) return null

        return (
          <Group align="center" gap="xs" wrap="nowrap">
            <TableRowActionButton
              Icon={IconMapPin}
              action="view"
              tooltipLabel="View History"
              color="port"
              onClick={() =>
                uiStore.openTableModal({
                  Component: AssetAssignmentsTable,
                  ComponentProps: {
                    where: {
                      or: [
                        { 'assignees.id': { equals: row.original.id } },
                        { 'creator.id': { equals: row.original.id } },
                      ],
                    },
                    TableProps: { enableTopToolbar: false },
                  },
                  TitleProps: {
                    title: `History for “${row.original.fullName}”`,
                    Icon: tableScreenIcon['assignments'],
                  },
                })
              }
            />
            <Text size="sm">{count}</Text>
          </Group>
        )
      },
    },
  ),
  columnHelper.accessor('internalNotes', {
    header: 'Notes For Supervisors',
    grow: true,
    minSize: 208,
    maxSize: 250,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.internalNotes
      }

      if (value === 'false') {
        return !row.original.internalNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => (
      <HeaderCellWithTooltip
        column={column}
        tooltip="Notes that can be used for internal purposes. Only visible to Supervisors."
      />
    ),
    Edit: props => {
      const mutation = useUserUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea
          {...props}
          updateFn={v => mutation.mutateAsync({ id: props.row.original.id, internalNotes: v })}
        />
      )
    },
  }),
  columnHelper.accessor('memberNotes', {
    header: 'Notes For Members',
    grow: true,
    maxSize: 250,
    minSize: 190,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.memberNotes
      }

      if (value === 'false') {
        return !row.original.memberNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => <HeaderCellWithTooltip column={column} tooltip="Notes displayed publicly to Members." />,
    Edit: props => {
      const mutation = useUserUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea {...props} updateFn={v => mutation.mutateAsync({ id: props.row.original.id, memberNotes: v })} />
      )
    },
  }),
]
